/**
 * Validates whether an email's local part (before the @) meets the
 * minimum username rules enforced by known email providers at account
 * creation. This catches addresses that are correctly *formatted* but
 * could never have actually been registered — e.g. `1@gmail.com`,
 * since Gmail requires at least 6 characters.
 *
 * Falls back to a generic RFC-ish check for domains not explicitly
 * handled (e.g. custom/business domains), since their username rules
 * are unknown and can't be enforced here.
 *
 * @param {string} email - The full email address to validate (e.g. "john.doe@gmail.com")
 * @returns {boolean} `true` if the local part satisfies the target provider's
 * username rules (or the generic fallback rule), `false` otherwise
 *
 * @example
 * isValidEmailByProvider('1@gmail.com');        // false (Gmail requires 6+ chars)
 * isValidEmailByProvider('johndoe@gmail.com');  // true
 * isValidEmailByProvider('ab@yahoo.com');       // false (Yahoo requires 4+ chars, must start with a letter)
 * isValidEmailByProvider('user@company.ph');    // true (falls back to generic check)
 */
export const isValidEmailByProvider = (email: string): boolean => {
  const [local, domain] = email.split('@');
  if (!local || !domain) return false;

  const domainLower = domain.toLowerCase();

  // Gmail: 6-30 chars, letters/numbers/dots only, can't start/end with dot
  if (domainLower === 'gmail.com' || domainLower === 'googlemail.com') {
    return /^[a-zA-Z0-9](?:[a-zA-Z0-9.]{4,28})[a-zA-Z0-9]$/.test(local);
  }

  // Yahoo: 4-32 chars, must start with a letter
  if (domainLower === 'yahoo.com') {
    return /^[a-zA-Z][a-zA-Z0-9_.]{3,31}$/.test(local);
  }

  // Outlook/Hotmail: 1-64 chars, fairly permissive
  if (['outlook.com', 'hotmail.com', 'live.com'].includes(domainLower)) {
    return /^[a-zA-Z0-9._-]{1,64}$/.test(local);
  }

  // Fallback: generic RFC-ish check for unknown domains
  return /^[a-zA-Z0-9._%+-]{1,64}$/.test(local);
};
